import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Box,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Stepper from "./Stepper";
import UploadCin7Dialog from "./UploadCin7Dialog";
import UploadAglcDialog from "./UploadAglcDialog";
import { AuthContext } from "../context/AuthProvider";

import { generateFilename } from "../utils/index.js";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor.js";

export default function PurchaseOrderProcessDialog({
  open,
  onClose,
  confirmationOrderId,
  initialStep = 0,
  initialProductsUrl = null,
  initialConfirmationUrl = null,
})  {
  const getInitialCompleted = () => {
    const completedSteps = {};
    if (initialProductsUrl) {
      completedSteps[0] = true;
      completedSteps[1] = true;
    }
    if (initialConfirmationUrl) {
      completedSteps[2] = true; 
      completedSteps[3] = true; 
    }
    return completedSteps;
  };

  const [activeStep, setActiveStep] = useState(initialStep);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [productsUrl, setProductsUrl] = useState(initialProductsUrl);
  const [confirmationUrl, setConfirmationUrl] = useState(initialConfirmationUrl);
  const [completed, setCompleted] = useState(getInitialCompleted());

  const auth = React.useContext(AuthContext);

  async function downloadFile(purchaseOrderId, fileField, fallbackUrl = null) {
    try {
      let fileUrl = fallbackUrl;

      if (!fileUrl) {
        const response = await fetchWithInterceptor(
          `/api/integrations/open-file?purchaseOrderId=${purchaseOrderId}&fileField=${fileField}`,
          {
            method: "GET",
            headers: {
              "x-access-token": auth.getToken(),
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch file URL");
        const data = await response.json();
        fileUrl = data.url;
      }

      if (!fileUrl || typeof fileUrl !== "string") {
        throw new Error(`Invalid or missing file URL for ${fileField}`);
      }

      const urlObj = new URL(fileUrl);
      const pathname = urlObj.pathname;
      let filename = pathname.split("/").pop();

      if (fileField === "productsUrl") {
        filename = `${generateFilename("products_to_order")}.csv`;
      } else if (fileField === "confirmationUrl") {
        filename = `${generateFilename("aglc_confirmation")}.csv`;
      } else if (fileField === "resultUrl") {
        filename = `${generateFilename("cin7_final")}.csv`;
      }

      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download error:", err);
    }
  }

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <Dialog onClose={onClose} aria-labelledby="purchase-order-process" open={open} fullScreen>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3 }}>
        <DialogTitle  component="h2">AGLC Purchase Order</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent dividers>
        <Box sx={{ px: 2, pt: 2, pb: 2, mb: 4}}>
          <Stepper activeStep={activeStep} completed={completed} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
         {activeStep === 0 && (
            productsUrl ? (
              <Button
                variant="outlined"
                onClick={() => {
                  downloadFile(
                    confirmationOrderId,
                    "url"
                  );
                  setCompleted((prev) => ({ ...prev, 0: true }));
                }}
              >
                Download Uploaded Cin7 CSV
              </Button>
            ) : (
              <UploadCin7Dialog
                onUploadSuccess={({ uploadedFileUrl, productsUrl }) => {
                  setUploadedFileUrl(uploadedFileUrl);
                  setProductsUrl(productsUrl);
                  setCompleted((prev) => ({ ...prev, 0: true }));
                  setActiveStep(4);
                  onClose(true)
                }}
              />
            )
          )}
         {activeStep === 1 && (
            <Button
              variant="outlined"
              onClick={() => {
                downloadFile(
                  confirmationOrderId,
                  "productsUrl",
                );
                setCompleted((prev) => ({ ...prev, 1: true }));
              }}
            >
              Download AGLC file
            </Button>
          )}
        {activeStep === 2 && (
          confirmationUrl ? (
            <Button
              variant="outlined"
              onClick={() => {
                downloadFile(
                  confirmationOrderId,
                  "confirmationUrl",
                );
                setCompleted((prev) => ({ ...prev, 2: true }));
              }}
            >
              Download AGLC Confirmation File
            </Button>
          ) : (
            <UploadAglcDialog
              confirmationOrderId={confirmationOrderId}
              onUploadSuccess={({ confirmationUrl }) => {
                setConfirmationUrl(confirmationUrl);
                setCompleted((prev) => ({ ...prev, 2: true }));
                setActiveStep(3);
                onClose(true)
              }}
            />
          )
        )}
          {activeStep === 3 && (
            <Button
              variant="outlined"
              onClick={() => {
                downloadFile(
                  confirmationOrderId,
                  "resultUrl"
                );
                setCompleted((prev) => ({ ...prev, 3: true }));
              }}
            >
              Download Cin7 file
            </Button>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleBack} disabled={activeStep === 0}>Back</Button>
        <Button
          onClick={() => {
            if (activeStep === 3) {
              onClose(true);
            } else {
              handleNext();
            }
          }}
          disabled={
            (activeStep === 0 && !uploadedFileUrl && !productsUrl) ||
            (activeStep === 1 && !productsUrl) ||
            (activeStep === 2 && !confirmationUrl)
          }
        >
          {activeStep === 3 ? "Finish" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}